import { useState } from "react";
import { Bug } from "lucide-react";
import { ModalShell } from "@/components/ModalShell";
import { useLocation } from "react-router-dom";
import { fnFetch } from "@/lib/fn-fetch";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { bugReference } from "@/lib/bug-reference";
import { composeBugDescription } from "@/lib/bug-report-text";
import { Field, SelectField, TextareaField } from "@/components/Field";

type Severity = "low" | "normal" | "high" | "critical";

// Pages where the floating button overlaps a primary action — hide it
const HIDDEN_ON_PATHS: string[] = ["/chat", "/kiosk"];

export function BugReportButton() {
  const { employee } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [expected, setExpected] = useState("");
  const [severity, setSeverity] = useState<Severity>("normal");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [notified, setNotified] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);

  if (!employee || employee.role === "kiosk") return null;
  if (HIDDEN_ON_PATHS.some(p => loc.pathname === p || loc.pathname.startsWith(p + "/"))) return null;

  function reset() {
    setTitle(""); setDescription(""); setExpected(""); setSeverity("normal");
    setScreenshot(null); setError(null); setDone(false); setNotified(false); setBusy(false);
    setReason(null); setReference(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!employee) return;
    setBusy(true); setError(null);
    try {
      let screenshot_path: string | null = null;
      if (screenshot) {
        const safeName = screenshot.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const path = `${employee.id}/${Date.now()}-${safeName}`;
        const { error: upErr } = await supabase.storage.from("bug-screenshots").upload(path, screenshot);
        if (upErr) throw new Error("Screenshot upload failed: " + upErr.message);
        screenshot_path = path;
      }
      const { data: inserted, error: insErr } = await supabase.from("hq_bug_reports").insert({
        title: title.trim(),
        description: composeBugDescription(description, expected),
        reporter_id: employee.id,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        screenshot_path,
        severity,
      }).select("id").single();
      if (insErr) throw new Error(insErr.message);

      // HQ-BUG-NOTIFY-001. This was fire-and-forget and the confirmation said
      // "Admin has been notified" regardless — including when RESEND_API_KEY
      // was missing and no mail was ever sent. The report is filed either way;
      // whether anyone has been told is a different fact, and worth waiting a
      // moment to state correctly.
      let delivered = false;
      let why: string | null = null;
      // From the id we just inserted, so it is shown even if the notify call fails.
      setReference(bugReference((inserted as { id: string }).id));
      try {
        const nr = await fnFetch("/.netlify/functions/bug-report-notify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bug_id: (inserted as { id: string }).id }),
        });
        const nj = await nr.json().catch(() => ({}));
        delivered = nr.ok && !!(nj as { delivered?: boolean }).delivered;
        why = (nj as { reason?: string }).reason ?? null;
      } catch { /* filed is filed; the message below says what happened */ }
      setNotified(delivered);
      setReason(why);

      // No auto-close: the reference is the point of this screen, and a message
      // that vanishes in under two seconds cannot be read, let alone copied.
      setDone(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/*
        ── WHY THIS IS SMALL AND QUIET ──────────────────────────────────────
        Reported from the field: "The Report a bug button is in the way of
        clicking next and might need to move it or shrink it."

        It was a full-width red pill pinned to the bottom-right corner — which
        is precisely where paginated tables put Next. A permanent control that
        covers the primary action of the screen underneath it is a control that
        competes with every task in HQ, and it was styled `bg-bad` besides, so
        it read as an alert on pages where nothing was wrong.

        It is now a small circular icon that expands to the labelled pill on
        hover and keyboard focus. Nothing was removed: the label is still there
        when a pointer or focus asks for it, the tooltip still names it, and it
        is still reachable by keyboard. It simply stops occupying the corner
        that the page beneath it needs.

        `group` + `focus-within` rather than hover alone, so a keyboard user
        gets the same affordance a mouse user does.
      */}
      <button
        onClick={() => setOpen(true)}
        title="Report a bug"
        aria-label="Report a bug"
        className="group fixed bottom-3 right-3 z-30 flex items-center gap-0 rounded-full border border-line bg-white/90 p-2.5 text-sm font-semibold text-gray2 shadow-md backdrop-blur transition-all hover:gap-2 hover:bg-bad hover:pr-4 hover:text-white focus-visible:gap-2 focus-visible:bg-bad focus-visible:pr-4 focus-visible:text-white md:bottom-4 md:right-4"
      >
        <Bug size={15} className="shrink-0" />
        <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all group-hover:max-w-[8rem] group-hover:opacity-100 group-focus-visible:max-w-[8rem] group-focus-visible:opacity-100">
          Report a bug
        </span>
      </button>

      {/* HQ-BUG-A11Y-001. The × here was an unnamed button — the accessibility
          tree showed "button" next to the heading and nothing else — and the
          dialog had no role, no Escape and no focus handling. ModalShell does
          all of that once, for every dialog in HQ. */}
      {open && (
        <ModalShell
          title="Report a bug"
          onClose={() => { setOpen(false); reset(); }}
          dismissable={!busy}
          closeLabel="Close report dialog"
        >
          <>
            {done ? (
              <div className="space-y-3 rounded-lg bg-ok-bg p-4 text-center text-sm text-ok">
                <p>
                  {notified
                    ? "Thanks — bug reported. An admin has been emailed."
                    : reason === "rate_limited"
                      ? "Thanks — bug reported. You've sent several in a row, so the admin wasn't emailed again; every one is on the bug list."
                      : "Thanks — bug reported. It's on the admin bug list; the email didn't go out, so mention it if it's urgent."}
                </p>
                {reference && (
                  <p>Your reference: <strong className="select-all font-mono">{reference}</strong> — quote it if you follow up.</p>
                )}
                <button type="button" className="btn" onClick={() => { setOpen(false); reset(); }}>Close</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <p className="text-xs text-gray2">
                  We auto-capture the page you're on ({loc.pathname}) and your browser info — no need to mention them.
                </p>
                {error && <div className="rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{error}</div>}
                <Field label="Short title" required maxLength={255} value={title}
                  onChange={e => setTitle(e.target.value)} placeholder="What broke?" autoFocus />
                <TextareaField label="What happened?" required className="input min-h-[96px]" value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="What were you trying to do, and what did you see instead?" />
                {/* HQ-BUG-INTAKE-002: without this a report cannot say what "wrong" meant. */}
                <TextareaField label="What did you expect to happen?" className="input min-h-[64px]" value={expected}
                  onChange={e => setExpected(e.target.value)}
                  help="Optional, but the quickest way to be understood."
                  placeholder="What should it have done?" />
                <div className="grid grid-cols-2 gap-3">
                  <SelectField label="Severity" value={severity} onChange={e => setSeverity(e.target.value as Severity)}>
                    <option value="low">Low — minor annoyance</option>
                    <option value="normal">Normal — affects my work</option>
                    <option value="high">High — blocks my work</option>
                    <option value="critical">Critical — blocks the team</option>
                  </SelectField>
                  <Field label="Screenshot (optional)" type="file" accept="image/*" className="block w-full text-xs text-gray2"
                    onChange={e => setScreenshot(e.target.files?.[0] ?? null)} />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => (setOpen(false), reset())} className="btn-secondary" disabled={busy}>Cancel</button>
                  <button type="submit" className="btn" disabled={busy}>{busy ? "Sending…" : "Submit"}</button>
                </div>
              </form>
            )}
          </>
        </ModalShell>
      )}
    </>
  );
}
